import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';

import logo from '../images/logo.webp';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null); // Ref for the mobile menu panel

  const navLinks = [
    { to: "/", text: "Home" },
    { to: "/menu", text: "Menú" },
    { to: "/wines", text: "Vinos" },
    { to: "/gastronomy", text: "Gastronomía" },
    { to: "/contact", text: "Contacto" },
  ];

  // Function to handle clicking outside the mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    // Add event listener when the mobile menu is open
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <header className="bg-gray-50 shadow-md">
      <nav className="container mx-auto flex items-center justify-between p-3">
        {/* Logo */}
        <Link to="/" className="text-gray-800 font-bebas text-3xl">
          <img src={logo} alt="Logo" className="h-16" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative font-montserrat text-md font-semibold tracking-wide uppercase transition-all duration-300 group ${location.pathname === link.to ? "text-black" : "text-gray-500"
                } hover:text-black`}
            >
              {link.text}
              {/* Underline with animation on hover */}
              <span
                className={`absolute bottom-[-3px] left-0 h-[1px] bg-black transition-all duration-300 w-0 group-hover:w-full ${location.pathname === link.to ? "w-full" : ""
                  }`}
              ></span>
            </Link>
          ))}
        </div>

        {/* Reservation Button */}
        <div className="hidden lg:flex">
          <Link
            to="/contact"
            className="uppercase font-montserrat font-bold text-sm bg-gray-800 text-white px-6 py-[9.5px] hover:scale-95 transform transition-all duration-300"
          >
            Reservar
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="lg:hidden p-2 text-gray-700 hover:text-black"
        >
          <Bars3Icon className="h-12 w-12" />
        </button>
      </nav>

      {/* Mobile Menu */}
      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className="lg:hidden"
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-10 bg-black bg-opacity-50 transition-opacity"
          aria-hidden="true"
        />

        <DialogPanel
          ref={mobileMenuRef}
          className="fixed inset-y-0 right-0 z-20 w-3/4 bg-white p-6 transform transition-transform"
          style={{
            transform: mobileMenuOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="text-black font-bebas text-3xl"
            >
              <img src={logo} alt="Logo" className="h-14" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-700 hover:text-black"
            >
              <XMarkIcon className="h-12 w-12" />
            </button>
          </div>
          <div className="space-y-4">
            {navLinks.map((link) => (
              <div key={link.to} className="relative">
                <Link
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block font-montserrat font-semibold text-md tracking-wide uppercase text-gray-600 hover:text-black hover:scale-95 transition-all duration-300 group" // Añade 'group' aquí
                >
                  {link.text}
                </Link>
              </div>
            ))}
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}